import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LayerData } from "../utils/brickOptimizer";
import "./LegoInstructionGenerator/styles/index.css";

// Import modularized components
import MultiStepGuide from "./LegoInstructionGenerator/MultiStepGuide";
import ProjectSummary from "./LegoInstructionGenerator/ProjectSummary";

// Import custom hooks
import { usePageNavigation } from "./LegoInstructionGenerator/usePageNavigation";
import { useLayerGeneration } from "./LegoInstructionGenerator/useLayerGeneration";

// Import utility
import { exportInstructions } from "./LegoInstructionGenerator/InstructionExport";

interface PixelData {
  pixels: number[][];
  width: number;
  height: number;
}

const LegoInstructionGenerator: React.FC<{
  pixelData?: PixelData;
  onLayersGenerated?: (layers: LayerData[]) => void;
  depthBands?: number;
  /** Pre-built layers (3D model flow) — bypasses image layer generation */
  layersOverride?: LayerData[];
}> = ({ pixelData, onLayersGenerated, depthBands = 1, layersOverride }) => {
  const { t } = useTranslation();
  const [currentLayer, setCurrentLayer] = useState(0);
  const [showInstructions, setShowInstructions] = useState(true);
  const [optimizationEnabled, setOptimizationEnabled] = useState(true);

  // Use custom hooks
  const generated = useLayerGeneration(pixelData ?? null, optimizationEnabled, onLayersGenerated, depthBands);
  const layers = layersOverride ?? generated;
  
  const currentLayerData = layers[currentLayer];
  const totalBricksInLayer = currentLayerData?.bricks.length || 0;
  const bricksPerPage = 3;
  
  const {
    currentPage,
    totalPages,
    handlePreviousPage,
    handleNextPage,
    handleTouchStart,
    handleTouchEnd,
  } = usePageNavigation(totalBricksInLayer, bricksPerPage);

  const totalLayers = layers.length;
  const totalBricks = layers.reduce((sum, layer) => sum + layer.brickCount, 0);

  // Plan extent: the image grid, or the bricks' own footprint for models
  const gridSize = pixelData
    ? { width: pixelData.width, height: pixelData.height }
    : {
        width: layers.reduce(
          (m, l) =>
            l.bricks.reduce(
              (mm, b) => Math.max(mm, b.x + b.brickType.width),
              m,
            ),
          0,
        ),
        height: layers.reduce(
          (m, l) =>
            l.bricks.reduce(
              (mm, b) => Math.max(mm, b.y + b.brickType.height),
              m,
            ),
          0,
        ),
      };
  
  // Get bricks for current page
  const startIndex = currentPage * bricksPerPage;
  const endIndex = Math.min(startIndex + bricksPerPage, totalBricksInLayer);
  const bricksOnPage = currentLayerData?.bricks.slice(startIndex, endIndex) || [];
  const allPreviousBricks = currentLayerData?.bricks.slice(0, startIndex) || [];

  return (
    <div className="lego-instruction-generator">
      <div className="instruction-header">
        <div className="header-content">
          <div className="header-title">
            <h2>{t("instructions.title")}</h2>
            <p className="header-subtitle">{t("instructions.subtitle")}</p>
          </div>
          <div className="instruction-controls">
            <label className="optimization-toggle">
              <input
                type="checkbox"
                checked={optimizationEnabled}
                onChange={(e) => setOptimizationEnabled(e.target.checked)}
              />
              <span>{t("controls.smartOptimization")}</span>
            </label>
            <button 
              type="button"
              className="btn-secondary"
              onClick={() => setShowInstructions(!showInstructions)}
            >
              {showInstructions ? t("instructions.hideInstructions") : t("instructions.showInstructions")}
            </button>
            <button 
              type="button"
              className="btn-primary"
              onClick={() => exportInstructions(layers)}
            >
              {t("instructions.exportInstructions")}
            </button>
          </div>
        </div>
      </div>

      {showInstructions && (
        <div className="step-by-step-instructions">
          <div className="instruction-info-bar">
            <div className="layer-selector-enhanced">
              <div className="selector-label">
                <span className="label-text">{t("instructions.currentLayer")}</span>
              </div>
              <select
                value={currentLayer}
                onChange={(e) => setCurrentLayer(Number(e.target.value))}
                className="layer-select"
              >
                {layers.map((layer) => (
                  <option key={layer.layer} value={layer.layer}>
                    {t("instructions.layerPieces", { index: layer.layer + 1, count: layer.bricks.length })}
                  </option>
                ))}
              </select>
            </div>
            <div className="progress-summary">
              <div className="summary-item">
                <span className="summary-text">
                  {t("instructions.pageOf", { current: currentPage + 1, total: totalPages })}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-text">
                  {t("instructions.piecesTotal", { count: totalBricksInLayer })}
                </span>
              </div>
            </div>
          </div>

          <MultiStepGuide
            currentPage={currentPage}
            totalPages={totalPages}
            bricksOnPage={bricksOnPage}
            allPreviousBricks={allPreviousBricks}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            bricksPerPage={bricksPerPage}
          />
        </div>
      )}

      {showInstructions && (
        <div className="instruction-tips">
          <h4>{t("instructions.quickTips")}</h4>
          <ul>
            <li><strong>{t("instructions.tipNavigation")}:</strong> {t("instructions.tipNavigationText")}</li>
            <li><strong>{t("instructions.tipKeyboard")}:</strong> {t("instructions.tipKeyboardText")}</li>
            <li><strong>{t("instructions.tipLayers")}:</strong> {t("instructions.tipLayersText")}</li>
            <li><strong>{t("instructions.tipPosition")}:</strong> {t("instructions.tipPositionText")}</li>
          </ul>
        </div>
      )}

      <ProjectSummary
        totalBricks={totalBricks}
        totalLayers={totalLayers}
        gridSize={gridSize}
        optimizationEnabled={optimizationEnabled}
      />
    </div>
  );
};

export default LegoInstructionGenerator;
